import React, { useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import stylez from "./userProfile.module.scss";
import { useUser, useHeaUser } from "@hooks";
import { useOnClickOutside } from "../../../hooks/useClickOutside";
import { useHistory } from "react-router-dom";
import DropDown from "../dropdown/Dropdown";
import { useLoginMutation } from "../../../hooks/useLoginMutation";
import { useHsaLoginMutation } from "../../../hooks/useHsaLoginMutation";
import { isHomeEDApp } from "@constants";
import Spinner from "@src/components/spinner/Spinner";
import { useAuthToken } from "@src/hooks/useAuthToken";
import AvatarIcon from "../../../assets/icons/avatar.svg?react";
import { useLessonFeedbackStatusStorage } from "@src/hooks/useLessonFeedbackStatusStorage";

type DropDownText = {
  logout: string;
  profile: string;
};

type Props = {
  mobileWidth?: number;
  menuText: DropDownText;
  isHidden?: boolean;
};

function UserProfile(props: Props) {
  const { mobileWidth, menuText, isHidden = false } = props;

  const isMobile = useMediaQuery({ maxWidth: mobileWidth || 600 });
  // const isTablet = useMediaQuery({minWidth: 799, maxWidth: 1023})

  let styles = stylez;

  const [user, { loading }] = useUser();
  const [heaUser, { loading: heaLoading }] = useHeaUser();
  const [, logout] = useLoginMutation();
  const [, hsaLogout] = useHsaLoginMutation();
  const [showMenu, setShowMenu] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { push } = useHistory();
  const { lockAccount } = useAuthToken();
  const { removeFeedbackStatus } = useLessonFeedbackStatusStorage();

  useOnClickOutside(wrapperRef, () => handleCloseMenu());

  const handleLogout = () => {
    handleCloseMenu();
    removeFeedbackStatus();
    logout?.();
    hsaLogout?.();
  };

  const handleOpenProfile = () => {
    push("/profile");
  };

  const handleToggleMenu = () => {
    if (isMobile) {
      return;
    }
    setShowMenu(!showMenu);
  };

  const handleCloseMenu = () => {
    setShowMenu(false);
  };

  const classes = useMemo(() => {
    const classes = [styles.wrapper];
    if (showMenu) {
      classes.push(styles.open);
    }
    return classes.join(" ");
  }, [showMenu, styles.wrapper, styles.open]);

  const profileSubmenu = [
    {
      title: menuText.profile,
      onClick: () => handleOpenProfile(),
    },
    {
      title: menuText.logout,
      onClick: () => handleLogout(),
    },
  ];

  const userFirstName = useMemo(() => {
    let firstName = isHomeEDApp ? heaUser?.firstName : user?.firstName;
    return firstName || "";
  }, [user, heaUser]);

  const userLastName = useMemo(() => {
    let lastName = isHomeEDApp ? heaUser?.lastName : user?.lastName;
    return lastName || "";
  }, [user, heaUser]);

  const userInitial = useMemo(() => {
    if (userLastName && userLastName.length > 0) {
      return `${userFirstName.charAt(0)}${userLastName.charAt(
        0
      )}`.toUpperCase();
    }
    if (userFirstName.length > 1) {
      return `${userFirstName.substring(2)}`.toUpperCase();
    }
    return `${userFirstName}${userLastName}`.toUpperCase();
  }, [userFirstName, userLastName]);

  return (
    <div
      ref={wrapperRef}
      className={classes}
      onClick={handleToggleMenu}
      style={{ display: isHidden ? "none" : "flex" }}
    >
      <div className={isHomeEDApp ? styles.heaContent : styles.content}>
        {!isMobile ? (
          lockAccount === "lock" && heaUser?.locked ? (
            <div className={styles.initialContainer}>
              <div className={styles.name} style={{ color: "#C4C4C4" }}>
                {userInitial ? null : <AvatarIcon />}
              </div>
            </div>
          ) : (
            <div className={styles.profileView}>
              <DropDown
                title={
                  <div
                    className={styles.initialContainer}
                    style={{
                      borderColor:
                        lockAccount === "lock" && heaUser?.locked
                          ? "#C4C4C4"
                          : "#959595",
                    }}
                  >
                    {loading ? (
                      <Spinner width={20} height={20} />
                    ) : (
                      <div className={styles.name} style={{ color: "#959595" }}>
                        {userInitial}
                      </div>
                    )}
                  </div>
                }
                list={profileSubmenu}
                isProfile={true}
              />
              {isHomeEDApp && (
                <div className={styles.avatarContainer}>
                  {heaLoading ? (
                    <Spinner width={20} height={20} />
                  ) : (
                    <AvatarIcon />
                  )}
                </div>
              )}
            </div>
          )
        ) : (
          <>
            <div className={styles.initialContainer}>
              {loading || heaLoading ? (
                <Spinner width={20} height={20} />
              ) : (
                <div className={styles.name}>
                  {userInitial ? userInitial : <AvatarIcon />}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default UserProfile;
